'use client';

import { usePathname } from 'next/navigation';
import { useAuth } from '@/context/AuthContext/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { useCommunityGuidelines } from '@/hooks/useCommunityGuidelines';
import { useAgeVerificationPending } from '@/hooks/useAgeVerification';
import { CommunityGuidelinesModal } from './CommunityGuidelinesModal';

/**
 * Drives the Community Guidelines modal (MWBI-668).
 *
 * Rule: on a signed-in user's first session, show the modal until they click
 * "I Agree". Acceptance is recorded server-side (acceptedCommunityGuidelinesAt);
 * once set, the modal never shows again — for that user, on any device.
 *
 * The modal is blocking by design: no onClose is passed, so there is no X
 * button, no backdrop dismiss, and no Escape. The only way out is accepting.
 */
export function CommunityGuidelinesGate() {
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const { accept, saving, error, hasAccepted } = useCommunityGuidelines();
  const pathname = usePathname();
  const agePending = useAgeVerificationPending();

  // The modal's "view full guidelines" link opens /guidelines-legal; suppress
  // the gate on that route so the page underneath stays readable before the
  // user has accepted.
  const onGuidelinesPage = pathname === '/guidelines-legal';

  return (
    <CommunityGuidelinesModal
      // Age comes first (MWBI-899): it decides which rules apply to this member,
      // so it is asked before the guidelines and the two blocking modals never
      // stack. Each gate stays keyed on its own field, so a member only ever sees
      // the screens they have not completed.
      isOpen={isAuthenticated && !hasAccepted && !onGuidelinesPage && !agePending}
      onAgree={accept}
      saving={saving}
      errorMessage={error ? t.modals.communityGuidelines.saveError : null}
    />
  );
}
